// ============================================================
// hooks/useFotoUsuario.ts
// Resuelve fotoPerfil a una URL renderizable en <img src>.
//
//   null                   → null  (caller muestra iniciales)
//   "http(s)://..."        → la URL tal cual (foto heredada externa)
//   "usuarios/<key>"       → blob:URL desde GET /usuarios/{id}/foto con JWT
//
// Caché en memoria por sesión: no refetcha si la key MinIO no cambió.
// invalidarFotoUsuario(id) borra el blob y fuerza nueva descarga
// (llámalo tras subir o eliminar foto).
// ============================================================

import { useEffect, useState } from 'react';
import { httpClient } from 'utils/httpClient';

interface CacheEntry {
  key:    string;            // "usuarios/<uuid>.jpg" — para detectar cambios
  url:    string;            // blob:URL generado
  loading: boolean;
}

// Caché compartida entre componentes durante la sesión
const cache = new Map<number, CacheEntry>();
// Inflight para evitar múltiples descargas simultáneas del mismo id
const inflight = new Map<number, Promise<string | null>>();
// Suscriptores por usuario para forzar re-render al invalidar
const subscribers = new Map<number, Set<() => void>>();

function notify(idUsuario: number): void {
  subscribers.get(idUsuario)?.forEach(fn => fn());
}

export function invalidarFotoUsuario(idUsuario: number): void {
  const prev = cache.get(idUsuario);
  if (prev?.url) URL.revokeObjectURL(prev.url);
  cache.delete(idUsuario);
  inflight.delete(idUsuario);
  notify(idUsuario);
}

async function descargarFoto(idUsuario: number, key: string): Promise<string | null> {
  if (inflight.has(idUsuario)) return inflight.get(idUsuario)!;
  const p = (async () => {
    try {
      const url = await httpClient.fetchBlobUrl(`/usuarios/${idUsuario}/foto`);
      cache.set(idUsuario, { key, url, loading: false });
      return url;
    } catch {
      cache.delete(idUsuario);
      return null;
    } finally {
      inflight.delete(idUsuario);
    }
  })();
  inflight.set(idUsuario, p);
  return p;
}

interface UseFotoResult {
  src:     string | null;     // URL renderizable (http o blob:) o null
  loading: boolean;
}

/**
 * Resuelve la foto del usuario para usar en <img src={src}>.
 * Pasa exactamente lo que devuelve el backend en `fotoPerfil`.
 */
export function useFotoUsuario(idUsuario: number | null | undefined, fotoPerfil: string | null): UseFotoResult {
  const [, force] = useState(0);
  const [resolved, setResolved] = useState<string | null>(null);
  const [loading, setLoading]   = useState(false);

  // Suscribir/desuscribir para invalidaciones externas
  useEffect(() => {
    if (!idUsuario) return;
    const fn = () => force(n => n + 1);
    let set = subscribers.get(idUsuario);
    if (!set) { set = new Set(); subscribers.set(idUsuario, set); }
    set.add(fn);
    return () => {
      set!.delete(fn);
      if (set!.size === 0) subscribers.delete(idUsuario);
    };
  }, [idUsuario]);

  useEffect(() => {
    let cancelled = false;

    if (!fotoPerfil || !idUsuario) {
      setResolved(null);
      setLoading(false);
      return;
    }

    // URL externa → directo
    if (fotoPerfil.startsWith('http://') || fotoPerfil.startsWith('https://')) {
      setResolved(fotoPerfil);
      setLoading(false);
      return;
    }

    // Key MinIO ("usuarios/...") → fetch autenticado, con caché
    const cached = cache.get(idUsuario);
    if (cached && cached.key === fotoPerfil) {
      setResolved(cached.url);
      setLoading(false);
      return;
    }

    // Si la key cambió, invalidar la entrada vieja antes de descargar
    if (cached && cached.key !== fotoPerfil) {
      URL.revokeObjectURL(cached.url);
      cache.delete(idUsuario);
    }

    setLoading(true);
    setResolved(null);
    descargarFoto(idUsuario, fotoPerfil).then(url => {
      if (cancelled) return;
      setResolved(url);
      setLoading(false);
    });

    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idUsuario, fotoPerfil]);

  return { src: resolved, loading };
}
